import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'
import { TemplateContext } from '../../../contexts'
import { Campaign, LocaleOption, Template } from '../../../types'
import { languageName } from '../../../utils'
import { useSearchParams } from 'react-router'

export const localeOption = (locale: string): LocaleOption => {
    const language = languageName(locale)
    return {
        key: locale,
        label: language ? `${language} (${locale})` : locale,
        shortLabel: language ? `${language}` : locale,
    }
}

export const locales = (templates: Template[]) => {
    const locales = [...new Set(templates.map(item => item.locale))]
    return locales.map(locale => localeOption(locale))
}

interface TemplateContextProviderParams {
    children: ReactNode
    campaign: Campaign
    setCampaign: Dispatch<SetStateAction<Campaign>>
}

export const TemplateContextProvider = ({ campaign, setCampaign, children }: TemplateContextProviderParams) => {
    const activeTemplates = campaign.templates
    const [template, setTemplate] = useState<Template | undefined>()
    const [previewTemplate, setPreviewTemplate] = useState<Template | undefined>()
    const [searchParams, setSearchParams] = useSearchParams()
    const templateId = searchParams.get('template')

    // A previewed template (e.g. an archived variant) is not part of the
    // campaign's active templates but still needs to render in the editor.
    const preview = previewTemplate && !activeTemplates.some(t => t.id === previewTemplate.id)
        ? previewTemplate
        : undefined
    const templates = preview ? [...activeTemplates, preview] : activeTemplates

    const handleSetTemplate = (template: Template | undefined) => {
        const nextTemplateId = template?.id.toString() ?? null
        if (nextTemplateId !== templateId) {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev)
                nextTemplateId === null
                    ? params.delete('template')
                    : params.set('template', nextTemplateId)
                return params
            }, { replace: true })
        }
        setPreviewTemplate(template && !activeTemplates.some(t => t.id === template.id) ? template : undefined)
        setTemplate(template)
    }

    useEffect(() => {
        const paramsTemplate = templates.find(t => `${t.id}` === templateId)

        // If we have a translation based on the URL params, use that
        if (paramsTemplate) setTemplate(paramsTemplate)

        // If no templates match the param check for other states
        else {

            // If we haven't set a template at all, pick the first one
            if (!template) {
                handleSetTemplate(activeTemplates[0])

            // If we have an ID but it hasn't matched any templates, clear
            } else if (templateId) {
                handleSetTemplate(undefined)
            }

            // Otherwise just let the template be
        }
    }, [campaign.id, campaign.templates.length, templateId])

    const templateManager = {
        campaign,
        setCampaign,
        currentTemplate: template,
        templates,
        currentLocale: template?.locale ? localeOption(template?.locale) : undefined,
        locales: locales(activeTemplates),
        variants: templates.filter(t => t.locale === template?.locale),
        variantMap: activeTemplates.reduce<Record<string, Template[]>>((map, template) => {
            if (!map[template.locale]) {
                map[template.locale] = []
            }
            map[template.locale].push(template)
            return map
        }, {}),
        setTemplate: handleSetTemplate,
        setLocale: (locale: LocaleOption | string | undefined) => {
            const key = typeof locale === 'string'
                ? locale
                : locale?.key
            const newTemplate = activeTemplates.find(t => t.locale === key)
            if (newTemplate) handleSetTemplate(newTemplate)
        },
    }

    return (
        <TemplateContext.Provider value={templateManager}>
            {children}
        </TemplateContext.Provider>
    )
}
